import type { ReactNode } from "react";
import {
  Outlet,
  createRootRoute,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/Layout";
import { AuthProvider } from "@/hooks/useAuth";
import { SiteIntegrations } from "@/components/SiteIntegrations";
import { useRouterState } from "@tanstack/react-router";
import { SITE_NAME, SITE_DESCRIPTION, buildSiteSchemas } from "@/data/seo";
import "@/index.css";

const queryClient = new QueryClient();

export const Route = createRootRoute({
  head: () => {
    const links = [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      {
        rel: "preconnect",
        href: "https://fonts.gstatic.com",
        crossOrigin: "anonymous",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@300;400;500;600&display=swap",
      },
    ];

    // In Vite dev, the CSS import is injected as unmanaged style tags.
    // Adding an explicit stylesheet link keeps the app styles under HeadContent control after hydration.
    if (import.meta.env.DEV) {
      links.unshift({
        rel: "stylesheet",
        href: "/src/index.css",
        type: "text/css",
      });
    }

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: SITE_NAME },
        {
          name: "description",
          content: SITE_DESCRIPTION,
        },
        { name: "theme-color", content: "#0d2328" },
        { name: "format-detection", content: "telephone=yes" },
        { property: "og:site_name", content: SITE_NAME },
        { property: "og:locale", content: "sv_SE" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links,
      scripts: buildSiteSchemas().map((schema) => ({
        type: "application/ld+json",
        children: JSON.stringify(schema),
      })),
    };
  },
  component: RootComponent,
});

function RootComponent() {
  const { location } = useRouterState();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <RootDocument>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner theme="dark" position="bottom-right" />
            {isAdmin ? (
              <Outlet />
            ) : (
              <>
                <SiteIntegrations />
                <Layout>
                  <Outlet />
                </Layout>
              </>
            )}
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </RootDocument>
  );
}

function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="sv">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}
